import { useRef, useState } from 'react'
import type { DragEvent } from 'react'
import type { MediaUploadResult } from '@cms/contracts'
import { ApiError, newIdempotencyKey } from '../api/client.js'
import { uploadMedia } from '../api/library.js'
import { ACCEPT_ATTRIBUTE, formatBytes, uploadProblem } from '../domain/media.js'
import { resolveReason } from '../domain/reason.js'
import { useI18n } from '../i18n/context.js'
import { ReasonNote } from '../ui/fields.js'
import { Button, Icon, IconButton, LinearProgress, cx } from '../ui/primitives.js'

type UploadState = 'waiting' | 'uploading' | 'done' | 'duplicate' | 'error'

interface UploadItem {
  key: number
  name: string
  size: number
  state: UploadState
  /** 0–1 while uploading. */
  progress: number
  message?: string
}

/**
 * Drag and drop or the file picker, several files at once, one after the
 * other with a progress bar each. One at a time on purpose: every upload is
 * a D1 batch of up to 10 MB on the gateway, and a person dropping twenty
 * photos should not turn into twenty of them at once.
 */
export function MediaUploader({ siteId, onUploaded, compact }: {
  siteId: string
  /** Called once per file the gateway accepted, duplicates included. */
  onUploaded: (result: MediaUploadResult) => void
  compact?: boolean
}) {
  const { t, locale } = useI18n()
  const input = useRef<HTMLInputElement | null>(null)
  const [items, setItems] = useState<UploadItem[]>([])
  const [dragging, setDragging] = useState(false)
  const [note, setNote] = useState('')
  const [running, setRunning] = useState(false)
  const counter = useRef(0)
  const noteRef = useRef(note)
  noteRef.current = note

  const patch = (key: number, change: Partial<UploadItem>) =>
    setItems((current) => current.map((item) => (item.key === key ? { ...item, ...change } : item)))

  const start = async (files: File[]) => {
    if (!files.length) return
    const queued = files.map((file) => {
      counter.current += 1
      const problem = uploadProblem(file)
      const item: UploadItem = {
        key: counter.current,
        name: file.name,
        size: file.size,
        state: problem ? 'error' : 'waiting',
        progress: 0,
        message: problem ? t(`media.upload.problems.${problem}`, { size: formatBytes(file.size, locale) }) : undefined,
      }
      return { item, file, problem }
    })
    setItems((current) => [...current.filter((item) => item.state === 'uploading' || item.state === 'waiting'), ...queued.map((entry) => entry.item)])
    setRunning(true)
    for (const { item, file, problem } of queued) {
      if (problem) continue
      patch(item.key, { state: 'uploading', progress: 0 })
      try {
        const result = await uploadMedia(
          siteId,
          { file, reason: resolveReason(noteRef.current, t('media.reason.upload', { name: file.name })) },
          newIdempotencyKey(),
          (loaded, total) => patch(item.key, { progress: total ? loaded / total : 0 }),
        )
        patch(item.key, {
          state: result.deduplicated ? 'duplicate' : 'done',
          progress: 1,
          message: result.deduplicated ? t('media.upload.duplicate', { path: result.asset.publicPath }) : result.asset.publicPath,
        })
        onUploaded(result)
      } catch (err) {
        patch(item.key, { state: 'error', message: err instanceof ApiError ? err.message : t('errors.network') })
      }
    }
    setRunning(false)
    setNote('')
  }

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    void start([...event.dataTransfer.files])
  }

  const finished = items.filter((item) => item.state !== 'waiting' && item.state !== 'uploading')

  return <div className="media-uploader">
    <div
      className={cx('dropzone', dragging && 'is-dragging', compact && 'dropzone-compact')}
      onDragOver={(event) => { event.preventDefault(); setDragging(true) }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <Icon name="cloud_upload" size={compact ? 24 : 32} className="dropzone-icon"/>
      <div className="dropzone-text">
        <strong>{t('media.upload.drop')}</strong>
        <span className="muted">{t('media.upload.limits')}</span>
      </div>
      <Button variant="tonal" icon="upload" onClick={() => input.current?.click()} disabled={running}>{t('media.upload.choose')}</Button>
      <input
        ref={input}
        type="file"
        multiple
        accept={ACCEPT_ATTRIBUTE}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => { const files = [...(event.target.files ?? [])]; event.target.value = ''; void start(files) }}
      />
    </div>
    {!compact && <ReasonNote value={note} onChange={setNote} fallback={t('media.reason.upload', { name: t('media.upload.fileNamePlaceholder') })} disabled={running}/>}
    {items.length > 0 && <ul className="upload-list" aria-live="polite">
      {items.map((item) => <li key={item.key} className={cx('upload-item', `is-${item.state}`)}>
        <Icon name={item.state === 'error' ? 'error' : item.state === 'done' || item.state === 'duplicate' ? 'check_circle' : 'upload'} className="upload-item-icon"/>
        <div className="upload-item-body">
          <div className="upload-item-head">
            <strong className="break">{item.name}</strong>
            <span className="muted">{formatBytes(item.size, locale)}</span>
          </div>
          {item.state === 'uploading' && <LinearProgress value={item.progress} label={t('media.upload.progress', { name: item.name })}/>}
          {item.state === 'waiting' && <span className="muted">{t('media.upload.waiting')}</span>}
          {item.message && <span className={cx('upload-item-message', item.state === 'error' && 'is-error', item.state !== 'error' && 'mono')}>{item.message}</span>}
        </div>
      </li>)}
    </ul>}
    {finished.length > 0 && !running && <div className="upload-list-actions">
      <IconButton icon="close" label={t('media.upload.clear')} onClick={() => setItems([])}/>
    </div>}
  </div>
}
